import React, { useState } from 'react';
import toast from 'react-hot-toast';
import {
  Plus,
  StickyNote,
  ListTodo,
  Sun,
  Reply,
  Hourglass,
  Users,
  Phone,
  Send,
  Lightbulb,
  Pin,
  CalendarRange,
  Inbox,
  Flag,
  ShoppingBag,
} from 'lucide-react';
import { usePlanner } from '../context/PlannerContext';
import StickyNoteCard from './StickyNoteCard';
import { STICKY_COLOR_KEYS, stickyStyle } from '../utils/stickyColors';
import { STICKY_BLOCKS, contentFromBlock } from '../utils/stickyBlocks';

const BLOCK_ICONS = {
  todo: ListTodo,
  today: Sun,
  week: CalendarRange,
  followup: Reply,
  waiting: Hourglass,
  meeting: Users,
  call: Phone,
  campaign: Send,
  inbox: Inbox,
  priority: Flag,
  idea: Lightbulb,
  errands: ShoppingBag,
};

const StickyNotesBoard = () => {
  const {
    notes,
    loadingNotes,
    createNote,
    updateNote,
    deleteNote,
    preferences,
    updatePreferences,
  } = usePlanner();
  const [creating, setCreating] = useState(false);
  const [newColor, setNewColor] = useState('butter');
  const [togglingOverlay, setTogglingOverlay] = useState(false);

  const handleCreate = async (preset = null) => {
    if (creating) return;
    setCreating(true);
    try {
      await createNote({
        body: preset ? contentFromBlock(preset) : '',
        color: preset?.color || newColor,
        width: preset?.width || 260,
        height: preset?.height || 280,
        showOnOverlay: false,
        rotation: Math.random() * 5 - 2.5,
      });
      toast.success(preset ? `„${preset.label}“ erstellt` : 'Notiz erstellt');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erstellen fehlgeschlagen');
    } finally {
      setCreating(false);
    }
  };

  const handleToggleOverlayPref = async (checked) => {
    setTogglingOverlay(true);
    try {
      await updatePreferences({ postitsAlwaysVisible: checked });
      toast.success(
        checked
          ? 'Angeheftete Notizen erscheinen auf allen Seiten'
          : 'Overlay aus — Notizen nur hier'
      );
    } catch (_) {
      toast.error('Einstellung konnte nicht gespeichert werden');
    } finally {
      setTogglingOverlay(false);
    }
  };

  const handlePin = async (note, showOnOverlay) => {
    try {
      await updateNote(note.id, { showOnOverlay });
      if (showOnOverlay && !preferences.postitsAlwaysVisible) {
        toast(
          (t) => (
            <div className="text-sm">
              <p className="font-medium">Angeheftet</p>
              <p className="text-neutral-400 text-xs mt-0.5">
                Aktiviere oben „Auf allen Seiten anzeigen“, damit die Notiz überall sichtbar ist.
              </p>
              <button
                type="button"
                className="mt-2 text-amber-600 dark:text-amber-400 font-semibold text-xs hover:underline"
                onClick={async () => {
                  toast.dismiss(t.id);
                  await handleToggleOverlayPref(true);
                }}
              >
                Jetzt aktivieren
              </button>
            </div>
          ),
          { duration: 6000, icon: '📌' }
        );
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Anheften fehlgeschlagen');
    }
  };

  return (
    <div className="h-full min-h-0 overflow-auto">
      <div className="sticky-board-page w-full max-w-none px-3 sm:px-4 lg:px-5 py-3 sm:py-4 lg:py-5">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-neutral-900 dark:text-white">
              Notizen
            </h1>
            <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
              Aufgaben und Follow-ups organisieren. Hefte eine Notiz an und aktiviere das Overlay,
              damit sie auf jeder Seite sichtbar bleibt.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <label className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white/80 dark:bg-neutral-900/60 cursor-pointer select-none">
              <Pin className={`w-3.5 h-3.5 ${preferences.postitsAlwaysVisible ? 'text-amber-600' : 'text-neutral-400'}`} />
              <span className="text-xs font-medium text-neutral-700 dark:text-neutral-200">
                Auf allen Seiten anzeigen
              </span>
              <input
                type="checkbox"
                className="rounded border-neutral-300 text-amber-600 focus:ring-amber-500"
                checked={!!preferences.postitsAlwaysVisible}
                disabled={togglingOverlay}
                onChange={(e) => handleToggleOverlayPref(e.target.checked)}
              />
            </label>
            <div className="flex items-center gap-1.5">
              {STICKY_COLOR_KEYS.map((key) => (
                <button
                  key={key}
                  type="button"
                  title={key}
                  onClick={() => setNewColor(key)}
                  className={`w-6 h-6 rounded-md transition-transform hover:scale-110 ${
                    newColor === key ? 'ring-2 ring-offset-2 ring-neutral-800 dark:ring-white dark:ring-offset-neutral-950' : ''
                  }`}
                  style={{ backgroundColor: stickyStyle(key).bg }}
                />
              ))}
            </div>
            <button
              type="button"
              onClick={() => handleCreate()}
              disabled={creating}
              className="btn-primary !py-2"
            >
              <Plus className="w-4 h-4 mr-1.5" />
              Leere Notiz
            </button>
          </div>
        </div>

        <div className="mb-7">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-neutral-400 mb-2">
            Vorlagen
          </p>
          <div className="flex flex-wrap gap-2">
            {STICKY_BLOCKS.map((block) => {
              const Icon = BLOCK_ICONS[block.id] || StickyNote;
              return (
                <button
                  key={block.id}
                  type="button"
                  disabled={creating}
                  onClick={() => handleCreate(block)}
                  className="sticky-block-chip"
                  title={block.hint}
                >
                  <span
                    className="sticky-block-chip__swatch"
                    style={{ backgroundColor: stickyStyle(block.color).bg }}
                  />
                  <Icon className="w-3.5 h-3.5 opacity-70" />
                  <span>{block.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {loadingNotes ? (
          <div className="py-20 flex justify-center">
            <div className="spinner" />
          </div>
        ) : notes.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-neutral-300 dark:border-neutral-700 px-6 py-16 text-center sticky-board-empty">
            <p className="text-neutral-700 dark:text-neutral-200 font-medium">Noch keine Notizen</p>
            <p className="text-sm text-neutral-500 mt-1">
              Starte mit einer Vorlage oder einer leeren Notiz — hefte sie an und aktiviere
              „Auf allen Seiten anzeigen“.
            </p>
            <button type="button" onClick={() => handleCreate()} className="btn-primary mt-5 !py-2">
              <Plus className="w-4 h-4 mr-1.5" />
              Erste Notiz erstellen
            </button>
          </div>
        ) : (
          <div className="sticky-board-grid">
            {notes.map((note, idx) => (
              <div
                key={note.id}
                className="sticky-board-slot"
                style={{ animationDelay: `${Math.min(idx, 8) * 40}ms` }}
              >
                <StickyNoteCard
                  note={note}
                  onChange={(patch) => updateNote(note.id, patch)}
                  onDelete={async () => {
                    try {
                      await deleteNote(note.id);
                      toast.success('Notiz gelöscht');
                    } catch (err) {
                      toast.error(err.response?.data?.error || 'Löschen fehlgeschlagen');
                    }
                  }}
                  onToggleDone={(done) => updateNote(note.id, { done })}
                  onToggleOverlay={(showOnOverlay) => handlePin(note, showOnOverlay)}
                  onResizeEnd={(size) => updateNote(note.id, size)}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default StickyNotesBoard;
